import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const ProtectedRoute = ({ children, requiredRole = null, adminOnly = false, adminOrEmployee = false }) => {
  const { dashboardUser, ecommerceUser, loading, isAuthenticated, isDashboardRoute } = useAuth();
  const location = useLocation();
  
  // Determine which user to check based on route
  const currentUser = isDashboardRoute ? dashboardUser : ecommerceUser;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Check if this is a dashboard route
    const isDashboardRoute = location.pathname.startsWith('/dashboard');
    console.log('🔍 [ProtectedRoute] User not authenticated, redirecting:', {
      pathname: location.pathname,
      isDashboardRoute,
      redirectTo: isDashboardRoute ? "/dashboard/login" : "/login"
    });
    // Redirect to appropriate login page with return url
    return <Navigate to={isDashboardRoute ? "/dashboard/login" : "/login"} state={{ from: location }} replace />;
  }

  if (adminOnly && currentUser?.role !== 'admin') {
    // Redirect to home page if user is not admin
    return <Navigate to="/" replace />;
  }

  if (adminOrEmployee && currentUser?.role !== 'admin' && currentUser?.role !== 'employee') {
    // Redirect to home page if user is not admin or employee
    // But don't redirect if they're trying to access the login page
    console.log('🔍 [ProtectedRoute] Checking admin/employee access:', {
      pathname: location.pathname,
      userRole: currentUser?.role,
      isDashboardLogin: location.pathname === '/dashboard/login',
      isAuthenticated,
      isDashboardRoute
    });
    
    // If user is authenticated but doesn't have the right role, redirect to home
    // If user is not authenticated, they should have been redirected already above
    if (location.pathname !== '/dashboard/login') {
      console.log('❌ [ProtectedRoute] User does not have admin/employee access, redirecting to home');
      return <Navigate to="/" replace />;
    }
    // If they're on the login page, let them stay (they'll see an error when trying to login)
    console.log('✅ [ProtectedRoute] User on login page, allowing access');
  }

  if (requiredRole && currentUser?.role !== requiredRole) {
    // Redirect to home page if user doesn't have required role
    return <Navigate to="/" replace />;
  }

  return children;
};

export default ProtectedRoute;
